import { ENDPOINTS } from '../constants/endpoints'
import type {
  InventoryCategoryCreateData,
  InventoryCategoryData,
  InventoryInTransactionCreateData,
  InventoryOutTransactionCreateData,
  InventoryStockCreateData,
  InventoryStockData,
  InventoryStockUpdateData,
  InventoryTransactionData,
} from '../schema/inventorySchema'

import type { AxiosResponse } from 'axios'

import axiosInstance from '@/integrations/axios/axios-instance'

export const inventoryApi = {
  // ===== Categories =====
  getCategories: (): Promise<
    AxiosResponse<{ categories: Array<InventoryCategoryData> }>
  > => {
    const endpoint = ENDPOINTS.LIST_CATEGORIES
    return axiosInstance.get(endpoint)
  },
  getCategory: (
    categoryId: string,
  ): Promise<AxiosResponse<{ category: InventoryCategoryData }>> => {
    const endpoint = `${ENDPOINTS.GET_CATEGORY}/${categoryId}`
    return axiosInstance.get(endpoint)
  },
  createCategory: (
    category: InventoryCategoryCreateData,
  ): Promise<
    AxiosResponse<{ message: string; category: InventoryCategoryData }>
  > => {
    const endpoint = ENDPOINTS.CREATE_CATEGORY
    return axiosInstance.post(endpoint, category)
  },
  updateCategory: (
    category: InventoryCategoryData,
  ): Promise<
    AxiosResponse<{ message: string; category: InventoryCategoryData }>
  > => {
    const categoryId = category._id
    const endpoint = `${ENDPOINTS.UPDATE_CATEGORY}/${categoryId}`
    return axiosInstance.put(endpoint, category)
  },
  deleteCategory: (
    categoryId: string,
  ): Promise<AxiosResponse<{ message: string; id: string }>> => {
    const endpoint = `${ENDPOINTS.DELETE_CATEGORY}/${categoryId}`
    return axiosInstance.delete(endpoint)
  },

  // ===== Stock (Inventory Quantities) =====
  getStockItems: (): Promise<
    AxiosResponse<{ stock: Array<InventoryStockData> }>
  > => {
    const endpoint = ENDPOINTS.LIST_STOCK
    return axiosInstance.get(endpoint)
  },
  getStockItem: (
    stockId: string,
  ): Promise<AxiosResponse<{ stock: InventoryStockData }>> => {
    const endpoint = `${ENDPOINTS.GET_STOCK}/${stockId}`
    return axiosInstance.get(endpoint)
  },
  createStock: (
    stock: InventoryStockCreateData,
  ): Promise<AxiosResponse<{ message: string; stock: InventoryStockData }>> => {
    const endpoint = ENDPOINTS.CREATE_STOCK
    return axiosInstance.post(endpoint, stock)
  },
  updateStock: (
    stockId: string,
    stock: InventoryStockUpdateData,
  ): Promise<AxiosResponse<{ message: string; stock: InventoryStockData }>> => {
    const endpoint = `${ENDPOINTS.UPDATE_STOCK}/${stockId}`
    return axiosInstance.put(endpoint, stock)
  },
  deleteStock: (
    stockId: string,
  ): Promise<AxiosResponse<{ message: string; id: string }>> => {
    const endpoint = `${ENDPOINTS.DELETE_STOCK}/${stockId}`
    return axiosInstance.delete(endpoint)
  },

  // ===== Transactions =====
  getTransactions: (): Promise<
    AxiosResponse<{ transactions: Array<InventoryTransactionData> }>
  > => {
    const endpoint = ENDPOINTS.LIST_TRANSACTIONS
    return axiosInstance.get(endpoint)
  },
  getTransaction: (
    transactionId: string,
  ): Promise<AxiosResponse<{ transaction: InventoryTransactionData }>> => {
    const endpoint = `${ENDPOINTS.GET_TRANSACTION}/${transactionId}`
    return axiosInstance.get(endpoint)
  },
  createInTransaction: (
    transaction: InventoryInTransactionCreateData,
    invoiceFile?: File,
  ): Promise<
    AxiosResponse<{
      message: string
      transaction: InventoryTransactionData
      stock: InventoryStockData
    }>
  > => {
    const endpoint = ENDPOINTS.CREATE_TRANSACTION

    // If there's an invoice file, send as FormData
    if (invoiceFile) {
      const formData = new FormData()
      formData.append('invoice', invoiceFile)
      formData.append('data', JSON.stringify(transaction))

      return axiosInstance.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    // Otherwise, send as JSON
    return axiosInstance.post(endpoint, transaction)
  },
  createOutTransaction: (
    transaction: InventoryOutTransactionCreateData,
  ): Promise<
    AxiosResponse<{
      message: string
      transaction: InventoryTransactionData
      stock: InventoryStockData
    }>
  > => {
    const endpoint = ENDPOINTS.CREATE_TRANSACTION
    return axiosInstance.post(endpoint, transaction)
  },

  downloadInvoice: (
    transactionId: string,
  ): Promise<
    AxiosResponse<{
      success: boolean
      downloadUrl: string
      fileName: string
    }>
  > => {
    const endpoint = `${ENDPOINTS.DOWNLOAD_INVOICE}/${transactionId}/invoice`
    return axiosInstance.get(endpoint)
  },
}
